// Filtros que sobreviven a irse de la pantalla y volver.
//
// Toda pantalla filtrable de esta aplicación escribe su estado de consulta en la
// query string (`?q=…&empresa=…`). Eso hace que un filtro sea enlazable y que
// aguante un refresco, pero NO que aguante irse a otra pantalla y volver: el
// enlace del menú lleva a la ruta pelada y la página se monta otra vez sin nada.
//
// Aquí está la regla que lo arregla, aislada de React para poder probarla sin
// navegador: se recuerda la query string de cada pantalla durante la SESIÓN y se
// repone SÓLO cuando se entra sin parámetros.
//
// ── Por qué la query string y no el estado de cada pantalla ──────────────────
// Porque ya existe, ya es el formato canónico de «los filtros de esta pantalla»
// y ya sabe serializar conjuntos, listas y booleanos. Persistir el estado de
// cada página habría sido escribir seis veces un serializador que ya está
// escrito, y además habría podido divergir de lo que dice la URL.
//
// ── Por qué `sessionStorage` y no `localStorage` ─────────────────────────────
// El mismo razonamiento que las marcas de fila (`utils/marcasFilas.ts`): un
// filtro es una nota de trabajo de la sesión de análisis en curso. Con
// `localStorage`, el usuario abriría el lunes el inventario y vería 3 filas de
// 4.000 por un filtro que puso hace tres semanas, sin nada en pantalla que le
// explique de dónde sale. Al cerrar la pestaña se acabó.
//
// ── Por qué manda el enlace ──────────────────────────────────────────────────
// Un enlace compartido, un marcador o el «volver» desde un detalle traen sus
// propios parámetros, y ésos son una intención explícita del que navega. Lo
// guardado es un recuerdo: nunca puede pisar una intención.

/** Lo mínimo que se le pide a `sessionStorage`.
 *
 *  Se declara aquí, y no se importa de `marcasFilas`, para que esta pieza no
 *  dependa de aquélla: son dos cosas que sólo coinciden en usar el mismo cajón
 *  del navegador. Al ser tipado estructural, el `sessionStorage` real y
 *  cualquier doble de test valen para las dos. */
// `AlmacenLike` vive en `types/index.ts`: lo comparten las marcas de fila y los
// filtros persistentes, y dos definiciones idénticas es una que se queda atrás.
import type { AlmacenLike } from '@/types'

export type { AlmacenLike }

/** Prefijo de la clave de almacenamiento. Distinto del de las marcas de fila
 *  (`marcas.`) para que limpiar filtros no pueda borrar marcas ni al revés. */
export const PREFIJO_FILTROS = 'filtros.'

/** De dónde salen los filtros con los que arranca la pantalla. */
export type OrigenFiltros = 'url' | 'sesion' | 'vacio'

/** `sessionStorage` puede lanzar sólo con tocarlo (modo privado, cookies
 *  bloqueadas por política). Devolver `null` deja la aplicación funcionando
 *  exactamente como antes de que esto existiera: sin recordar nada. */
export function almacenSesion(): AlmacenLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** Query string guardada de un ámbito. Cualquier fallo devuelve la cadena vacía:
 *  perder unos filtros nunca puede tumbar la pantalla. */
export function leerFiltros(ambito: string, storage: AlmacenLike | null): string {
  if (!storage) return ''
  try {
    return storage.getItem(PREFIJO_FILTROS + ambito) ?? ''
  } catch {
    return ''
  }
}

/**
 * Guarda la query string de un ámbito.
 *
 * Guardar la cadena vacía BORRA la entrada. Es lo que hace que «Limpiar
 * filtros» no deje al usuario atrapado sin escribir una sola línea en cada
 * pantalla: limpiar vacía la URL, la URL vacía borra el recuerdo, y al volver la
 * pantalla sale limpia. La invariante es siempre la misma —lo guardado es lo que
 * dice la URL— y por eso no puede desincronizarse.
 */
export function guardarFiltros(
  ambito: string,
  query: string,
  storage: AlmacenLike | null,
): void {
  if (!storage) return
  try {
    if (query) storage.setItem(PREFIJO_FILTROS + ambito, query)
    else storage.removeItem(PREFIJO_FILTROS + ambito)
  } catch {
    /* sin almacenamiento no se recuerda nada, que es el comportamiento de antes */
  }
}

/** Olvida lo guardado de un ámbito. */
export function olvidarFiltros(ambito: string, storage: AlmacenLike | null): void {
  if (!storage) return
  try {
    storage.removeItem(PREFIJO_FILTROS + ambito)
  } catch {
    /* ignorado a propósito */
  }
}

/** ¿Trae esta query string algún parámetro? */
function tieneParametros(query: string): boolean {
  for (const _ of new URLSearchParams(query).keys()) return true
  return false
}

/**
 * La regla completa, en una función pura.
 *
 * `busqueda` es el `location.search` con el que se entra; `guardado`, lo que la
 * sesión recuerda de esa pantalla. Gana la URL en cuanto trae UN parámetro:
 * media docena de campos vacíos y uno relleno siguen siendo una intención
 * explícita, y completarla con lo de la sesión daría una pantalla que no es ni
 * la del enlace ni la de antes.
 */
export function resolverFiltros(
  busqueda: string,
  guardado: string,
): { query: string; origen: OrigenFiltros } {
  if (tieneParametros(busqueda)) {
    return { query: new URLSearchParams(busqueda).toString(), origen: 'url' }
  }
  if (tieneParametros(guardado)) {
    return { query: new URLSearchParams(guardado).toString(), origen: 'sesion' }
  }
  return { query: '', origen: 'vacio' }
}

// ── Ruta recordada: volver al DETALLE donde se estaba ────────────────────────
// Misma doctrina, otra mitad: cuando el estado vive en una RUTA propia (el detalle
// de un pedido: pedido en el path, pestaña y filtros en la query), lo que se
// recuerda es la URL entera y se repone sólo si se entra a la lista pelada.

/** Guarda la ruta actual bajo `clave`. Nunca lanza. */
export function guardarRutaRecordada(
  clave: string,
  pathname: string,
  search: string,
  storage: AlmacenLike | null,
): void {
  if (!storage) return
  try {
    storage.setItem(clave, pathname + search)
  } catch {
    /* sin almacenamiento no se recuerda nada */
  }
}

/** Ruta a la que saltar al entrar, o `null` si manda la URL o no hay recuerdo. */
export function rutaAReponer(
  clave: string,
  busqueda: string,
  storage: AlmacenLike | null,
): string | null {
  if (tieneParametros(busqueda)) return null
  if (!storage) return null
  try {
    return storage.getItem(clave) || null
  } catch {
    return null
  }
}

/** Olvida la ruta recordada: «he terminado con este detalle». */
export function olvidarRutaRecordada(clave: string, storage: AlmacenLike | null): void {
  if (!storage) return
  try {
    storage.removeItem(clave)
  } catch {
    /* ignorado a propósito */
  }
}

/** Clave del recuerdo de la ruta del detalle de pedido de venta. Prefijo propio, como el del scroll. */
export const RUTA_DETALLE_PEDIDO_KEY = 'pedidosVentas:detalle'
